import { useNavigate, useSearchParams } from 'react-router-dom'
import { PROCEDURE_FILTER_COLUMNS } from 'app/components/action-center/procedureFilter'
import { ACTION_CENTER_FLAG } from 'app/components/action-center/systemPipelines'
import { CustomPageForm } from 'app/components/contacts/CustomPageForm'
import { platformFilterValue } from 'app/filter.utils'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListCopy } from 'app/hooks/useListCopy'
import { showModal } from 'app/lib/modal'
import { notifySuccess } from 'app/services/notify.client'
import { getFilterableColumns as activitiesFilterableColumns } from 'app/table-columns/activities'
import { appointmentFilterableColumns } from 'app/table-columns/appointments'
import { filterableColumns as contactsFilterableColumns } from 'app/table-columns/contacts'
import { getFilterableColumns as tasksFilterableColumns } from 'app/table-columns/tasks'
import type { List } from 'app/generated/graphql'
import type { CustomPageJson, CustomPages } from 'app/types/entities'
import { useUser } from 'app/utils'
import { titleCase } from 'app/utils/titleCase'
import { EMPTY_PROVIDER_DIRECTORY } from 'lib/appointmentsPage'
import { comActivityTypes } from 'lib/comActivityTypes'
import { isSMCOrg } from 'lib/isSMCOrg'
import { RECORD_TYPES } from 'lib/recordTypes'

export function useOpenCustomPageEditor() {
  const user = useUser()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const actionCenterEnabled = useFeatureToggle(ACTION_CENTER_FLAG)
  const copy = useListCopy()
  const checkPermission = useCheckPermission()

  return ({
    customPage,
    list,
    pageType,
    onSaved,
    onDeleted,
  }: {
    customPage: CustomPageJson
    /** The List the saved list lives on; without one the edit goes to the saved page. */
    list?: List
    pageType: CustomPages
    onSaved: () => void
    onDeleted: () => void
  }) => {
    if (!user) return
    const isSuper = user.type === `super`
    const isSMC = isSMCOrg(user.orgId!)
    const addMarketingFilters = isSuper || checkPermission(`org:marketing:advanced-filters`)
    const isNotOrgAdmin = !checkPermission([`org:settings:manage-users`])
    const filters =
      pageType === `contacts`
        ? contactsFilterableColumns({
            isOrgView: !user.workspaceId,
            type: platformFilterValue(searchParams),
            isSMC,
            isSuper,
            isWokspaceUser: isNotOrgAdmin,
            actionCenterEnabled,
            checkPermission,
            copy,
          })
        : pageType === `activities`
          ? activitiesFilterableColumns({
              comActivityTypes,
              addMarketingColumns: addMarketingFilters,
              addFollowUp: true,
            })
          : pageType === `treatmentPlans`
            ? PROCEDURE_FILTER_COLUMNS
            : pageType === `appointments`
              ? // The rule editor has no provider list, so its Provider filter offers no names.
                appointmentFilterableColumns({ preAppliedFilters: {}, providers: EMPTY_PROVIDER_DIRECTORY })
              : tasksFilterableColumns()

    showModal(
      <CustomPageForm
        page={pageType}
        subpage={customPage}
        saveTo={list ? `list` : `customPage`}
        list={list}
        filters={filters}
        onPageCreated={onSaved}
        onDelete={() => {
          notifySuccess(`${titleCase(customPage.name)} page deleted`)
          onDeleted()
          if (searchParams.get(`tab`) === customPage.id) {
            navigate(`${RECORD_TYPES[pageType].basePath}?tab=all`, { replace: true })
          }
        }}
      />,
      { title: `Edit List`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
    )
  }
}
